import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** True after this instance applied scroll-lock styles in showDialog (showModal succeeded). */
  #bodyLockActive = false;

  /** Removes the per-dialog `close` listener; aborted before programmatic closeDialog. */
  #dialogCloseAbortController = /** @type {AbortController | null} */ (null);

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    this.#dialogCloseAbortController?.abort();
    this.#dialogCloseAbortController = null;
    // Ensure body styles are restored when component is disconnected
    this.#restoreBodyStyles();
  }

  /**
   * Fires on the live <dialog> (native close, Escape, backdrop). Ensures scroll lock is
   * cleared when closeDialog() was not used — e.g. UA light-dismiss. Host capture does not
   * receive `close` reliably across browsers.
   * @param {Event} event
   */
  #handleDialogClose = (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!this.contains(dialog)) return;
    if (dialog.open) return;

    if (!this.#bodyLockActive && document.body.style.position !== 'fixed') return;

    requestAnimationFrame(() => {
      if (dialog.open) return;
      this.#restoreBodyStyles();
      if (this.#previousScrollY > 0) {
        window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      }
    });
  };

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Restores body and HTML element styles to their default state
   * Used as a safeguard when dialogs fail to open or close properly
   */
  #restoreBodyStyles() {
    this.#bodyLockActive = false;
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.style.height = '';
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog: initialDialog } = this.refs;
    if (!initialDialog || initialDialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      // Re-read refs inside the frame: cart section morph (cart:update) can replace
      // the <dialog> node in the same turn as open(); a stale closure would lock
      // the body and call showModal() on a detached dialog (broken scroll / no UI).
      const { dialog } = this.refs;
      if (!dialog || !dialog.isConnected) {
        this.#restoreBodyStyles();
        return;
      }

      // Another showDialog() may have opened the dialog in a prior frame; do not
      // restore body styles while the dialog is open (that breaks the modal).
      if (dialog.open) return;

      // Store the current document height before fixing body position
      // This prevents HTML element from collapsing to 0 height
      const documentHeight = document.documentElement.scrollHeight;
      
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      
      // Ensure HTML element maintains its height when body is fixed
      // This prevents the page from becoming unscrollable/unclickable
      if (!document.documentElement.style.height) {
        document.documentElement.style.height = `${documentHeight}px`;
      }

      try {
        dialog.showModal();
        // Verify dialog actually opened - if not, restore body styles
        if (!dialog.open) {
          this.#restoreBodyStyles();
          return;
        }

        this.#bodyLockActive = true;
        this.#dialogCloseAbortController?.abort();
        this.#dialogCloseAbortController = new AbortController();
        dialog.addEventListener('close', this.#handleDialogClose, {
          signal: this.#dialogCloseAbortController.signal,
        });

        this.dispatchEvent(new DialogOpenEvent());

        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      } catch (error) {
        // If showModal fails, restore body styles immediately
        console.warn('Dialog failed to open:', error);
        this.#restoreBodyStyles();
      }
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    // Prefer the live child <dialog>; refs can point at a replaced node after section morph
    // while the modal the user sees (and the close button) is still [open].
    const scoped = this.querySelector(':scope > dialog');
    const dialog =
      scoped instanceof HTMLDialogElement ? scoped : this.refs.dialog;

    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    this.#dialogCloseAbortController?.abort();
    this.#dialogCloseAbortController = null;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    const closeAnimationCapMs = 800;

    try {
      // Force browser to restart animation by resetting it
      dialog.style.animation = 'none';
      void dialog.offsetWidth;
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';

      await Promise.race([
        onAnimationEnd(dialog, undefined, { subtree: true }),
        new Promise((resolve) => setTimeout(resolve, closeAnimationCapMs)),
      ]);
    } catch {
      // getAnimations / finished rejected; still tear down in finally
    } finally {
      try {
        this.#restoreBodyStyles();
        window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      } catch (error) {
        this.#restoreBodyStyles();
        console.warn('Dialog close: Error restoring scroll position', error);
      }

      // Section morph (e.g. cart line update) can replace <dialog> while we await the close
      // animation; closing only the stale ref leaves the new in-document dialog [open].
      const scopedLive = this.querySelector(':scope > dialog');
      const live =
        scopedLive instanceof HTMLDialogElement ? scopedLive : dialog;

      const closeIfOpen = (/** @type {HTMLDialogElement} */ el) => {
        if (!el.open) return;
        try {
          el.close();
        } catch {
          el.removeAttribute('open');
        }
      };

      if (dialog instanceof HTMLDialogElement && dialog !== live) {
        closeIfOpen(dialog);
      }

      if (live instanceof HTMLDialogElement) {
        closeIfOpen(live);
        live.classList.remove('dialog-closing');
      } else if (dialog instanceof HTMLDialogElement) {
        dialog.classList.remove('dialog-closing');
      }

      try {
        this.dispatchEvent(new DialogCloseEvent());
      } catch {
        // ignore
      }
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName, { bubbles: true, composed: true });
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName, { bubbles: true, composed: true });
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
